/**
 * Coming back to Teslyn Cloud from a sign-in (decision 801, docs/specs/cloud/sign-in-security.md §3). Teslyn Cloud
 * signs in with PKCE, so what Supabase hands back is one of:
 *
 * - `?code=…` (and `sb_flow_id=…`): an email link, Microsoft, Google, LinkedIn or an email change asked for in this
 *   browser. supabase-js exchanges it with the code verifier it kept here; a code from a link asked for in another
 *   browser has no verifier here and cannot be used, which is the point of PKCE: the page says so.
 * - `?token_hash=…&type=…`: every email the Send Email hook writes (sign-in link, invitation, email change; decision
 *   890, packages/domain auth-email.ts `authLinkUrl`) links here with its token hash, instead of Supabase's
 *   `/auth/v1/verify`, which spends the token on a GET: a mail scanner that opens links (Microsoft 365's Safe Links,
 *   3 Nov 2026) spent MB's sign-in and its code before he read the email. This page spends nothing on load: it shows
 *   a small screen with one button ("Sign in to Teslyn", `PendingLink`), and only that press exchanges the hash with
 *   `verifyOtp` (a POST; the session comes in the response body, never in the address). A `pkce_` hash is exchanged
 *   the same way: Supabase's POST `/auth/v1/verify` takes the hash as stored, prefix and all, and needs no code
 *   verifier (the verifier is only for the `?code=` that Supabase's GET link hands out); so the link signs in
 *   whichever browser presses the button, as the email's code does (§3.1 of the spec).
 * - `#access_token=…&refresh_token=…&type=invite`: an invitation. Supabase makes invitations without a browser asking
 *   (the inviter's function does), so they cannot carry a PKCE code and come back with the tokens in the fragment.
 *   supabase-js refuses those in PKCE mode; this module takes them, removes them from the address first, and checks
 *   the access token with Supabase before keeping the session.
 * - `error=…`: a refusal, read by `readAuthError` on the sign-in page.
 *
 * Everything this module reads is removed from the address, so a reload or the history never holds it.
 *
 * A token hash or an invitation never replaces a sign-in this browser already has with another account (security
 * review M3, 2 Nov 2026): a link someone else sent could otherwise sign the person into the sender's account without
 * their noticing. The token hash is exchanged on a separate client that keeps nothing (`asideClient`); for the same
 * person the page carries on with its sign-in (refreshed, for an email change), for another account that session is
 * ended at once and the page says who is signed in and what the link was for.
 */
import type { EmailOtpType, SupabaseClient } from '@supabase/supabase-js';
import { asideClient } from '../supabase.ts';
import { explain } from './authError.ts';

export type AuthReturn =
  | { readonly kind: 'none' }
  | { readonly kind: 'error' }
  | { readonly kind: 'code' }
  | { readonly kind: 'token-hash'; readonly tokenHash: string; readonly type: EmailOtpType }
  | {
      readonly kind: 'fragment';
      readonly accessToken: string;
      readonly refreshToken: string;
      readonly type: string | null;
    };

/**
 * An email link opened in this browser, not yet spent: the page shows its button and spends it with `finish` (the
 * exchange, under the M3 rules) or takes it out of the address with `dismiss`.
 */
export interface PendingLink {
  readonly kind: 'link';
  readonly type: EmailOtpType;
  /** Spend the link. The words to show when it did not sign the person in, or null. */
  readonly finish: () => Promise<string | null>;
  /** Leave the link unspent (it still works until it expires): only remove it from the address. */
  readonly dismiss: () => void;
}

/** What a return leaves for the page: words for the sign-in page, a link waiting for its button, or nothing. */
export type AuthOutcome = string | PendingLink | null;

export const isPendingLink = (x: AuthOutcome | undefined): x is PendingLink =>
  typeof x === 'object' && x !== null && x.kind === 'link';

/** The confirm screen's words for a link: the button is the one the email names. */
export function linkWords(type: EmailOtpType): {
  readonly title: string;
  readonly button: string;
  readonly body: string;
} {
  switch (type) {
    case 'invite':
      return {
        title: 'Your invitation to Teslyn',
        button: 'Accept the invitation',
        body: 'You opened an invitation from a Teslyn email. Press the button to make your sign-in and open Teslyn.',
      };
    case 'signup':
      return {
        title: 'Confirm your email address',
        button: 'Confirm my email address',
        body: 'You opened a link from a Teslyn email. Press the button to confirm this email address.',
      };
    case 'email_change':
      return {
        title: 'Confirm your new email address',
        button: 'Confirm the change',
        body: 'You opened a link from a Teslyn email. Press the button to confirm the change of email address.',
      };
    default:
      return {
        title: 'Sign in to Teslyn',
        button: 'Sign in to Teslyn',
        body: 'You opened a sign-in link from a Teslyn email. Press the button to sign in.',
      };
  }
}

/** Said under the button: the page does nothing by itself, so a link a scanner opened is still the person's. */
export const LINK_UNSPENT_WORDS =
  'Nothing happens until you press it. If you did not ask for this email, press "Not now" and ignore the email.';

/** The email link types `verifyOtp` takes with a token hash. */
const OTP_TYPES: readonly EmailOtpType[] = [
  'invite',
  'magiclink',
  'email',
  'signup',
  'recovery',
  'email_change',
];

/** The address parts a return carries; removed once read. */
export const RETURN_QUERY_KEYS = ['code', 'sb_flow_id', 'token_hash', 'type'] as const;
const FRAGMENT_KEYS = [
  'access_token',
  'refresh_token',
  'expires_in',
  'expires_at',
  'token_type',
  'type',
  'provider_token',
  'provider_refresh_token',
];

function fragmentParams(hash: string): URLSearchParams {
  return new URLSearchParams(hash.replace(/^#/, ''));
}

/** What this address brings back from a sign-in, if anything. */
export function readAuthReturn(loc: { readonly search: string; readonly hash: string }): AuthReturn {
  const q = new URLSearchParams(loc.search);
  const h = fragmentParams(loc.hash);
  if (q.has('error') || q.has('error_description') || h.has('error') || h.has('error_description'))
    return { kind: 'error' };
  const tokenHash = q.get('token_hash');
  const type = q.get('type');
  if (tokenHash && type && (OTP_TYPES as readonly string[]).includes(type))
    return { kind: 'token-hash', tokenHash, type: type as EmailOtpType };
  const accessToken = h.get('access_token');
  const refreshToken = h.get('refresh_token');
  if (accessToken && refreshToken)
    return { kind: 'fragment', accessToken, refreshToken, type: h.get('type') };
  if (q.get('code')) return { kind: 'code' };
  return { kind: 'none' };
}

/** The address without the return's parts (other parameters such as `?tenant=` and `?page=` stay). */
export function addressWithoutReturn(href: string): string {
  const url = new URL(href);
  // `type` belongs to the return only beside a token hash
  const withHash = url.searchParams.has('token_hash');
  for (const k of RETURN_QUERY_KEYS) if (k !== 'type' || withHash) url.searchParams.delete(k);
  const h = fragmentParams(url.hash);
  if (FRAGMENT_KEYS.some((k) => h.has(k))) {
    for (const k of FRAGMENT_KEYS) h.delete(k);
    const rest = h.toString();
    url.hash = rest ? `#${rest}` : '';
  }
  return url.pathname + url.search + url.hash;
}

/** The words for a code that this browser cannot use: the link was asked for in another browser or device. */
export const OTHER_BROWSER_WORDS =
  'This sign-in link was asked for in another browser or on another device, so it cannot sign you in here. ' +
  'Open the link in the browser where you asked for it, or ask for a new link below.';

type Win = Pick<Window, 'location' | 'history'>;

/** The words when a link is for another account than the one this browser is signed in with (M3). */
export function otherAccountWords(signedIn: string, link: string | null): string {
  return (
    `This link is for ${link ? link : 'another account'}, but this browser is signed in as ${signedIn || 'someone else'}. ` +
    `Nothing changed: you are still signed in as ${signedIn || 'before'}. To use the link, sign out first, then ask ` +
    'for a new one.'
  );
}

/**
 * Finish a sign-in this address brings back. Returns the words to show on the sign-in page when it did not sign the
 * person in, a `PendingLink` for an email link (spent only by its button), or null (signed in, nothing came back, or
 * a refusal the sign-in page explains itself).
 */
export async function finishAuthReturn(
  sb: SupabaseClient,
  win: Win = window,
  aside: () => SupabaseClient = asideClient,
): Promise<AuthOutcome> {
  const r = readAuthReturn(win.location);
  if (r.kind === 'none' || r.kind === 'error') return null;
  const clean = (): void =>
    win.history.replaceState(win.history.state, '', addressWithoutReturn(win.location.href));
  if (r.kind === 'fragment') clean(); // the tokens leave the address before anything else runs
  // supabase-js exchanges a code it holds the verifier for while it starts (detectSessionInUrl)
  const started = await sb.auth.initialize();
  if (r.kind === 'code') {
    const stillThere = new URLSearchParams(win.location.search).has('code');
    clean();
    if (started.error) return explain(started.error.message);
    if (!stillThere) return null; // exchanged and removed by supabase-js
    const { data } = await sb.auth.getSession();
    return data.session ? null : OTHER_BROWSER_WORDS;
  }
  if (r.kind === 'token-hash') {
    // nothing is spent on load: a mail scanner's visit must leave the link and its code usable
    return {
      kind: 'link',
      type: r.type,
      dismiss: clean,
      finish: () => spendTokenHash(sb, r.tokenHash, r.type, clean, aside),
    };
  }
  const current = (await sb.auth.getSession()).data.session;
  if (current) {
    const { data, error } = await sb.auth.getUser(r.accessToken);
    if (error || !data.user) return explain(error?.message ?? 'Invalid token');
    if (data.user.id !== current.user.id)
      return otherAccountWords(current.user.email ?? '', data.user.email ?? null);
  }
  const { error } = await sb.auth.setSession({ access_token: r.accessToken, refresh_token: r.refreshToken });
  return error ? explain(error.message) : null;
}

/**
 * Spend an email link's token hash (the button was pressed): `verifyOtp` signs this browser in, or, when it is
 * signed in already, says whose link it is without touching that sign-in (M3). The address is cleaned first, so a
 * reload never spends it twice.
 */
async function spendTokenHash(
  sb: SupabaseClient,
  tokenHash: string,
  type: EmailOtpType,
  clean: () => void,
  aside: () => SupabaseClient,
): Promise<string | null> {
  clean();
  const current = (await sb.auth.getSession()).data.session;
  if (!current) {
    const { error } = await sb.auth.verifyOtp({ token_hash: tokenHash, type });
    return error ? explain(error.message) : null;
  }
  // signed in already: find out whose link it is without touching this browser's sign-in
  const side = aside();
  const { data, error } = await side.auth.verifyOtp({ token_hash: tokenHash, type });
  if (error) return explain(error.message);
  const linkUser = data.user ?? data.session?.user ?? null;
  // the link's own session is not needed either way: this browser keeps the one it has
  if (data.session) await side.auth.signOut({ scope: 'local' }).catch(() => undefined);
  if (linkUser && linkUser.id === current.user.id) {
    await sb.auth.refreshSession().catch(() => undefined); // an email change shows at once
    return null;
  }
  return otherAccountWords(current.user.email ?? '', linkUser?.email ?? null);
}
